import React, { useState, useMemo, useCallback, useEffect } from 'react'
import Table from '@cloudscape-design/components/table'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import TextFilter from '@cloudscape-design/components/text-filter'
import Link from '@cloudscape-design/components/link'

import ImportDialog from './import-dialog'
import AddDocumentDialog from './add-document-dialog'
import EditDocumentDialog from './edit-document-dialog'
import LocationCell from './location-cell'
import TitleCell from './title-cell'
import ExportAuditButton from './export-audit-button'
import ErrorBoundary from '../common/error-boundary'
import { exportToCSV } from '../../utils/csv'
import { processCSVImport } from '../../utils/import'
import UpdateDocument from '../../api/update-document'
import DeleteDocument from '../../api/delete-document'
import './table-styles.css'

/**
 * EnhancedTable Component - Cloudscape Version
 * 
 * Main container component that orchestrates all table functionality including:
 * - Column filtering with debounced input
 * - Pagination for large datasets
 * - CSV import/export for librarians
 * - Role-based access control
 * - Document owner can edit their own documents
 */
const EnhancedTable = ({ data = [], token, isLibrarian = false, username = '', onImportComplete, apiEndpoint, onDataChange, settings }) => {
  // Extract username without email domain for comparison
  const usernameWithoutDomain = username.split('@')[0]
  
  // Filter state - Cloudscape uses single filter text
  const [filteringText, setFilteringText] = useState('')

  // Pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(1)
  const pageSize = 20

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importData, setImportData] = useState(null)
  const [importErrors, setImportErrors] = useState([])
  const [isProcessingImport, setIsProcessingImport] = useState(false)

  // Add document dialog state
  const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false)
  const [isProcessingAdd, setIsProcessingAdd] = useState(false)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingDocument, setEditingDocument] = useState(null)
  const [isProcessingEdit, setIsProcessingEdit] = useState(false)

  // Handle CSV import file selection
  const handleImportFile = useCallback(async (file) => {
    const existingDIDs = data.map(record => record.did)
    const result = await processCSVImport(file, existingDIDs)

    if (result.success) {
      setImportData(result.records)
      setImportErrors([])
      setImportDialogOpen(true)
    } else {
      setImportData(null)
      setImportErrors(result.errors)
      setImportDialogOpen(true)
    }
  }, [data])

  // Handle import confirmation
  const handleImportConfirm = useCallback(async () => {
    if (!importData || importData.length === 0) return

    setIsProcessingImport(true)

    try {
      if (onImportComplete) {
        await onImportComplete(importData)
      }
      setImportDialogOpen(false)
      setImportData(null)
      setImportErrors([])
    } catch (error) {
      console.error('Import error:', error)
      let errorMessage = error.message || 'Import failed. Please try again.'
      
      if (error.message.includes('Authentication failed')) {
        errorMessage = 'Your session has expired. Please refresh the page and log in again.'
      } else if (error.message.includes('Permission denied')) {
        errorMessage = 'You do not have permission to import data. Librarian access is required.'
      }
      
      setImportErrors([{
        row: 0,
        column: 'Import',
        message: errorMessage,
        value: ''
      }])
    } finally {
      setIsProcessingImport(false)
    }
  }, [importData, onImportComplete])

  // Handle import dialog close
  const handleImportDialogClose = useCallback(() => {
    if (!isProcessingImport) {
      setImportDialogOpen(false)
      setImportData(null)
      setImportErrors([])
    }
  }, [isProcessingImport])

  // Handle add document
  const handleAddDocument = useCallback(() => {
    setAddDocumentDialogOpen(true)
  }, [])

  // Handle add document submit
  const handleAddDocumentSubmit = useCallback(async (documentData) => {
    setIsProcessingAdd(true)

    try {
      const records = [documentData]
      if (onImportComplete) {
        await onImportComplete(records)
      }
      setAddDocumentDialogOpen(false)
    } catch (error) {
      console.error('Add document error:', error)
      alert(`Failed to add document: ${error.message}`)
    } finally {
      setIsProcessingAdd(false)
    }
  }, [onImportComplete])

  // Handle add document dialog close
  const handleAddDocumentDialogClose = useCallback(() => {
    if (!isProcessingAdd) {
      setAddDocumentDialogOpen(false)
    }
  }, [isProcessingAdd])

  // Handle DID click (librarians or document owners)
  const handleDIDClick = useCallback((document) => {
    // Check if user is librarian or owns the document
    const isOwner = document.owner === usernameWithoutDomain
    if (isLibrarian || isOwner) {
      setEditingDocument(document)
      setEditDialogOpen(true)
    }
  }, [isLibrarian, usernameWithoutDomain])

  // Track if we're preserving pagination state after an update/delete
  const [preservePagination, setPreservePagination] = useState(false)

  // Handle document update
  const handleDocumentUpdate = useCallback(async (updatedData) => {
    if (!apiEndpoint) {
      alert('API endpoint not configured')
      return
    }

    setIsProcessingEdit(true)

    try {
      await UpdateDocument(token, apiEndpoint, updatedData)
      setEditDialogOpen(false)
      setEditingDocument(null)

      // Mark that we want to preserve pagination state
      setPreservePagination(true)

      if (onDataChange) {
        await onDataChange()
      }
    } catch (error) {
      console.error('Update document error:', error)
      alert(`Failed to update document: ${error.message}`)
    } finally {
      setIsProcessingEdit(false)
    }
  }, [apiEndpoint, token, onDataChange])

  // Handle document delete
  const handleDocumentDelete = useCallback(async (did) => {
    if (!apiEndpoint) {
      alert('API endpoint not configured')
      return
    }

    setIsProcessingEdit(true)

    try {
      await DeleteDocument(token, apiEndpoint, did)
      setEditDialogOpen(false)
      setEditingDocument(null)

      // Mark that we want to preserve pagination state
      setPreservePagination(true)

      if (onDataChange) {
        await onDataChange()
      }
    } catch (error) {
      console.error('Delete document error:', error)
      alert(`Failed to delete document: ${error.message}`)
    } finally {
      setIsProcessingEdit(false)
    }
  }, [apiEndpoint, token, onDataChange])

  // Handle edit dialog close
  const handleEditDialogClose = useCallback(() => {
    if (!isProcessingEdit) {
      setEditDialogOpen(false)
      setEditingDocument(null)
    }
  }, [isProcessingEdit])

  // Get existing DIDs for duplicate checking
  const existingDIDs = useMemo(() => {
    return data.map(record => record.did)
  }, [data])

  // Apply filters and sort data
  const filteredData = useMemo(() => {
    if (!filteringText) {
      return [...data].sort((a, b) => {
        const didA = a.did || ''
        const didB = b.did || ''
        return didA.localeCompare(didB, undefined, { numeric: true, sensitivity: 'base' })
      })
    }

    const lowerFilter = filteringText.toLowerCase()
    const filtered = data.filter(record => {
      return (
        record.did?.toLowerCase().includes(lowerFilter) ||
        record.title?.toLowerCase().includes(lowerFilter) ||
        record.location?.toLowerCase().includes(lowerFilter) ||
        record.approvalId?.toLowerCase().includes(lowerFilter) ||
        record.owner?.toLowerCase().includes(lowerFilter) ||
        record.status?.toLowerCase().includes(lowerFilter)
      )
    })

    return filtered.sort((a, b) => {
      const didA = a.did || ''
      const didB = b.did || ''
      return didA.localeCompare(didB, undefined, { numeric: true, sensitivity: 'base' })
    })
  }, [data, filteringText])

  // Reset to page 1 when filter changes (but not when preserving pagination after update/delete)
  useEffect(() => {
    if (!preservePagination) {
      setCurrentPageIndex(1)
    }
  }, [filteringText, preservePagination])

  // Reset preservePagination flag after data changes
  useEffect(() => {
    if (preservePagination) {
      setPreservePagination(false)
    }
  }, [data, preservePagination])

  // Adjust current page if it's now out of bounds after data changes (e.g., after delete)
  useEffect(() => {
    const totalPages = Math.ceil(filteredData.length / pageSize)
    if (currentPageIndex > totalPages && totalPages > 0) {
      setCurrentPageIndex(totalPages)
    }
  }, [filteredData.length, currentPageIndex, pageSize])

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPageIndex - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, currentPageIndex, pageSize])

  // Handle CSV export
  const handleExport = useCallback(() => {
    const exportData = filteredData.map(record => ({
      DID: record.did,
      Title: record.title,
      Filename: record.filename,
      'Authoritative URL': record.location,
      'Approval ID': record.approvalId || '',
      Owner: record.owner,
      Status: record.status
    }))

    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    const filename = `kmsdid-export-${year}-${month}-${day}.csv`

    exportToCSV(exportData, filename)
  }, [filteredData])

  // Column definitions for Cloudscape Table
  const columnDefinitions = useMemo(() => [
    {
      id: 'did',
      header: 'DID',
      cell: item => {
        // Make DID clickable for librarians or document owners
        const isOwner = item.owner === usernameWithoutDomain
        return (isLibrarian || isOwner) ? (
          <Link onFollow={() => handleDIDClick(item)}>{item.did}</Link>
        ) : item.did
      },
      sortingField: 'did',
      width: 150,
      minWidth: 120
    },
    {
      id: 'title',
      header: 'Title',
      cell: item => <TitleCell title={item.title} maxLength={40} />,
      sortingField: 'title',
      width: 210,
      minWidth: 140
    },
    {
      id: 'location',
      header: 'Authoritative URL',
      cell: item => <LocationCell url={item.location} maxLength={45} />,
      width: 300,
      minWidth: 200
    },
    {
      id: 'approvalId',
      header: 'Approval ID',
      cell: item => item.approvalId ? (
        <Link external href={`https://approvals.amazon.com/approval/${item.approvalId}`}>{item.approvalId}</Link>
      ) : '',
      sortingField: 'approvalId',
      width: 140,
      minWidth: 100
    },
    {
      id: 'owner',
      header: 'Owner',
      cell: item => item.owner,
      sortingField: 'owner',
      width: 90,
      minWidth: 60
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => item.status,
      sortingField: 'status',
      width: 150,
      minWidth: 100
    }
  ], [isLibrarian, usernameWithoutDomain, handleDIDClick])

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <Box textAlign="center" color="inherit" padding="l">
        <SpaceBetween size="m">
          <Box variant="strong" fontSize="heading-m">No data available</Box>
          <Box variant="p">There are no documents to display.</Box>
        </SpaceBetween>
      </Box>
    )
  }

  return (
    <>
      {/* Add Document Dialog */}
      <AddDocumentDialog
        isOpen={addDocumentDialogOpen}
        onClose={handleAddDocumentDialogClose}
        onSubmit={handleAddDocumentSubmit}
        existingDIDs={existingDIDs}
        isProcessing={isProcessingAdd}
      />

      {/* Edit Document Dialog (Librarians and Document Owners) */}
      <EditDocumentDialog
        isOpen={editDialogOpen}
        onClose={handleEditDialogClose}
        onUpdate={handleDocumentUpdate}
        onDelete={handleDocumentDelete}
        document={editingDocument}
        isProcessing={isProcessingEdit}
        isLibrarian={isLibrarian}
      />

      {/* Import Dialog */}
      <ImportDialog
        isOpen={importDialogOpen}
        onClose={handleImportDialogClose}
        onConfirm={handleImportConfirm}
        recordCount={importData?.length || 0}
        errors={importErrors}
        isProcessing={isProcessingImport}
      />

      <ErrorBoundary fallbackMessage="The data table encountered an error.">
        <div className="kmsdid-table">
          <Table
            columnDefinitions={columnDefinitions}
            items={paginatedData}
            loadingText="Loading documents..."
            sortingDisabled
            empty={
              <Box textAlign="center" color="inherit">
                <Box variant="strong" textAlign="center" color="inherit">
                  No documents found
                </Box>
                <Box variant="p" padding={{ bottom: 's' }} color="inherit">
                  {filteringText ? 'No documents match your search.' : 'No documents to display.'}
                </Box>
              </Box>
            }
            filter={
            <TextFilter
              filteringText={filteringText}
              filteringPlaceholder="Search documents"
              filteringAriaLabel="Filter documents"
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
            />
          }
          header={
            <Header
              counter={`(${filteredData.length}/${data.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handleExport}>Export CSV</Button>
                  {isLibrarian && (
                    <>
                      <Button onClick={() => document.getElementById('import-file-input')?.click()}>
                        Import CSV
                      </Button>
                      <ExportAuditButton 
                        isLibrarian={isLibrarian} 
                        token={token} 
                        settings={settings} 
                      />
                      <Button variant="primary" onClick={handleAddDocument}>
                        Add Document
                      </Button>
                    </>
                  )}
                </SpaceBetween>
              }
            >
              Documents
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={Math.ceil(filteredData.length / pageSize)}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
        />
        </div>
      </ErrorBoundary>

      {/* Hidden file input for CSV import */}
      {isLibrarian && (
        <input
          id="import-file-input"
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              handleImportFile(file)
              e.target.value = '' // Reset input
            }
          }}
        />
      )}
    </>
  )
}

export default EnhancedTable
